import { Breadcrumbs } from "@intility/bifrost-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

// Content column width, matching the Bifrost docs site. The header band's
// background stretches full-bleed across the nav-content area; this only
// constrains the inner content — breadcrumbs, title, and body — so everything
// lines up on one centred column down the page.
const CONTENT_MAX_WIDTH = 1170;

export interface PageCrumb {
  label: string;
  /** Omit on the last (current) crumb so it renders as plain text. */
  to?: string;
}

/**
 * Title size, used to signal depth. Top-level pages (Platforms, Clients)
 * use level 1; a level down (a platform, a model version) uses 2; deeper still
 * (a client nested under a platform) uses 3. The element stays an `<h1>` for
 * accessibility — only the visual size changes.
 */
export type PageLevel = 1 | 2 | 3;

const TITLE_CLASS: Record<PageLevel, string> = {
  1: "bf-h1",
  2: "bf-h2",
  3: "bf-h3",
};

interface PageLayoutProps {
  /** Home / Section / Current — the last crumb is usually link-less. */
  breadcrumbs?: PageCrumb[];
  /** Omit for a page that is only breadcrumbs under the header band. */
  title?: ReactNode;
  /** Rendered inline after the title (e.g. a role/status `<Badge>`). */
  badge?: ReactNode;
  /** Right-aligned controls in the title row (e.g. a primary action button). */
  actions?: ReactNode;
  description?: ReactNode;
  /** Title size signalling page depth (default 1). See {@link PageLevel}. */
  level?: PageLevel;
  /**
   * Let the body span the full nav-content width instead of the 1170 column —
   * for graph/tree canvases that need the whole canvas. The header band always
   * stays on the 1170 column.
   */
  fullWidthBody?: boolean;
  /**
   * Override the centred column width (px). Defaults to {@link CONTENT_MAX_WIDTH}.
   * The docs guide opts into a wider column for its three-column layout.
   */
  maxWidth?: number;
  children?: ReactNode;
}

/**
 * The portal's page shell: a full-bleed `base-2` header band (breadcrumbs →
 * title + badge → description) over a centred content column. Reference style
 * modelled on the Bifrost docs site; see the portal AGENTS.md for the rules.
 */
export function PageLayout({
  breadcrumbs,
  title,
  badge,
  actions,
  description,
  level = 1,
  fullWidthBody = false,
  maxWidth = CONTENT_MAX_WIDTH,
  children,
}: PageLayoutProps) {
  const hasCrumbs = !!breadcrumbs?.length;

  return (
    <div className="bf-content">
      <header
        style={{
          backgroundColor: "var(--bfc-base-2)",
          borderBottom: "1px solid var(--bfc-base-c-dimmed)",
        }}
      >
        <div
          className="bf-page-padding-y"
          style={{ maxWidth, marginInline: "auto" }}
        >
          {hasCrumbs && (
            <Breadcrumbs>
              {breadcrumbs?.map((crumb) => (
                <Breadcrumbs.Item key={crumb.label}>
                  {crumb.to ? (
                    <Link to={crumb.to}>{crumb.label}</Link>
                  ) : (
                    crumb.label
                  )}
                </Breadcrumbs.Item>
              ))}
            </Breadcrumbs>
          )}
          {(title || badge || actions) && (
            <div
              style={{
                marginTop: hasCrumbs ? 24 : 0,
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <h1 className={TITLE_CLASS[level]} style={{ margin: 0 }}>
                {title}
              </h1>
              {badge}
              {actions && <div style={{ marginLeft: "auto" }}>{actions}</div>}
            </div>
          )}
          {description && (
            <p
              className="bf-p bfc-base-2"
              style={{ marginTop: 8, marginBottom: 0, maxWidth: "72ch" }}
            >
              {description}
            </p>
          )}
        </div>
      </header>

      <div
        className={fullWidthBody ? "bf-page-padding" : "bf-page-padding-y"}
        style={fullWidthBody ? undefined : { maxWidth, marginInline: "auto" }}
      >
        {children}
      </div>
    </div>
  );
}
